import { createInstance, type i18n as I18n } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LOCALE, loadMessages, type Messages } from './config';

declare module 'i18next' {
  interface CustomTypeOptions {
    // t('landing.headline') is type-checked against en.json.
    resources: { translation: Messages };
  }
}

/** A fresh i18next instance with the locale (and the English fallback) already loaded. */
export async function createI18n(locale: string): Promise<I18n> {
  const resources: Record<string, { translation: Messages }> = {
    [locale]: { translation: await loadMessages(locale) },
  };
  if (locale !== DEFAULT_LOCALE) {
    resources[DEFAULT_LOCALE] = { translation: await loadMessages(DEFAULT_LOCALE) };
  }

  const instance = createInstance();
  await instance.use(initReactI18next).init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    resources,
    interpolation: { escapeValue: false }, // React already escapes output
    initAsync: false,
  });
  return instance;
}

/** Switch the language at runtime, loading its messages on first use. */
export async function switchLocale(instance: I18n, locale: string): Promise<void> {
  if (!instance.hasResourceBundle(locale, 'translation')) {
    instance.addResourceBundle(locale, 'translation', await loadMessages(locale));
  }
  await instance.changeLanguage(locale);
}
